import React, { useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  image_service_url as imageRoot,
  destinations,
  CLUSTER
} from '../../constants';
import CommentsContext from '../../contexts/comments';
import DestinationContext from '../../contexts/destination';
import { useTranslation } from '../../i18n';
import { isAddYours } from '../../utils/isAddYours';
import { isDiscussionPage } from '../../utils/isDiscussionPage';
import { isGeneratorPage } from '../../utils/isGeneratorPage';
import { isNews } from '../../utils/isNews';
import { isSponsored } from '../../utils/isSponsored';
import { useCommentsTracking } from '../../hooks/analytics/useCommentsTracking';
import { useCommentsCtaButtonTracking } from '../../hooks/analytics/useCommentsCtaButtonTracking';
import { SpeechBubbleOutlined } from '../Svgs/SpeechBubbleOutlined';
import { WaveDivider } from '../Svgs/WaveDivider';
import dynamicImport from '../../utils/dynamic-import';
import EmojiReactions from '../EmojiReactions';
import SectionTitle from '../SectionTitle';
import withAds from './withAds';
import styles from './index.module.scss';

const Comments = dynamicImport(
  () =>
    import(
      /* webpackChunkName: "comments" */ '@buzzfeed/react-components/dist/module/lib/components/Comments'
    ),
  { ssr: false }
);

const CommentsCtaWrapper = dynamicImport(
  () =>
    import(
      /* webpackChunkName: "comments-cta-wrapper" */ '../CommentsCtaWrapper'
    ),
  { ssr: false }
);

const CondensedCommentsWrapper = dynamicImport(
  () =>
    import(
      /* webpackChunkName: "condensed-comments" */ '../CondensedCommentsWrapper'
    ),
  { ssr: false }
);

function CommentsWrapper({
  buzz,
  commentCount,
  firstPage,
  repliesFirstPage,
  perPage,
  isAdsInComments,
  onAdPlaceholderRefsReady,
  onCommentsLoaded,
  showEmojiReactions = false,
}) {
  const { base_url: apiRoot, destination } = useContext(DestinationContext);
  const {
    commentsEnabled,
    isCommentsPanelOpen,
    toggleCommentsPanel,
  } = useContext(CommentsContext);
  const { t } = useTranslation('common');
  const isUS = buzz.classification?.edition === 'US' || !buzz.classification?.edition;

  const [ctaState, setCtaState] = useState({
    ctaCommentText: '',
    syncCtaToSidebar: false,
    syncSidebarToCta: false,
  });
  const condensedComments = !isDiscussionPage(buzz)
    && !isGeneratorPage(buzz)
    && !buzz.isCommentsPage
    && buzz.classification.edition !== 'Japan';
  const shouldShowCommentsCta = condensedComments;
  const { base_url: bfBaseUrl } = destinations['buzzfeed'];
  const commentsTrackingFn = useCommentsTracking(buzz);
  const commentsBottomCtaButtonTrackingFn = useCommentsCtaButtonTracking(buzz, {
    unit_type: 'buzz_bottom',
    subunit_name: 'comments_cta_section'
  });
  const loginRedirectUrl = isNews(buzz)
    ? `${apiRoot}/auth/sso/signin?redirect=${buzz.canonical_url}`
    : undefined;
  const locale = buzz.language || 'en';

  const onCloseComments = useCallback(() => {
    toggleCommentsPanel(false);
  }, [toggleCommentsPanel]);

  const onOpenComments = useCallback(() => toggleCommentsPanel(true), [toggleCommentsPanel]);

  const commentsApiRoot = CLUSTER === 'dev' ? bfBaseUrl : apiRoot;
  const comments = <Comments
    apiRoot={commentsApiRoot}
    profileRoot={bfBaseUrl}
    imageRoot={imageRoot}
    buzzId={+buzz.id}
    firstPage={firstPage}
    repliesFirstPage={repliesFirstPage}
    perPage={perPage}
    track={commentsTrackingFn}
    locale={locale}
    loginRedirectUrl={loginRedirectUrl}
    onLoaded={onCommentsLoaded}
    allowImages={isAddYours(buzz)}
    className={classNames({
      'themeNews': isNews(buzz),
      'condensedComments': condensedComments,
      // This is used to hide the header in discussion and generator pages
      'discussionPage': isDiscussionPage(buzz) || isGeneratorPage(buzz),
    })}
    showCloseButton={condensedComments}
    isAdsInComments={isAdsInComments}
    isCommentFormSticky={false}
    onAdPlaceholderRefsReady={onAdPlaceholderRefsReady}
    onCloseButtonClick={onCloseComments}
    onOpenComments={onOpenComments}
    shouldSortByDate={buzz.tags.includes('--sort-comments-latest')}
    ctaState={ctaState}
    hideCommentCount={isGeneratorPage(buzz)}
    typesFilter={isGeneratorPage(buzz) ? ['-ai_generation'] : null}
  />;

  if (!showEmojiReactions && !commentsEnabled) {
    return null;
  }

  if (!commentsEnabled && showEmojiReactions) {
    return (
      <EmojiReactions contentId={buzz.id} isSponsored={isSponsored(buzz)} showTitle={true} />
    );
  }

  return (
    <div className={classNames(styles.commentsWrapper, {
      [styles['ctaWrapper']]: shouldShowCommentsCta,
      [styles['themeNews']]: isNews(buzz),
    })}>
      {!condensedComments && (
        <div id="comments" className={styles.comments}>
          {comments}
        </div>
      )}
      {shouldShowCommentsCta && (
        <>
          {!isNews(buzz) && !isUS && (
            <div className={styles.divider}>
              <WaveDivider aria-hidden="true" />
            </div>
          )}
          <SectionTitle className={styles.title} id="reactions-title"><SpeechBubbleOutlined className={styles.icon}/>{t('comments')}</SectionTitle>
          {showEmojiReactions && <EmojiReactions contentId={buzz.id} isSponsored={isSponsored(buzz)} />}
          <CommentsCtaWrapper
            buzz={buzz}
            profileRoot={bfBaseUrl}
            commentCount={commentCount}
            commentsApiRoot={commentsApiRoot}
            locale={locale}
            destination={destination}
            ctaState={ctaState}
            setCtaState={setCtaState}
            onOpenComments={onOpenComments}
            toggleCommentsPanel={toggleCommentsPanel}
            isCommentsPanelOpen={isCommentsPanelOpen}
            commentsBottomCtaButtonTrackingFn={commentsBottomCtaButtonTrackingFn}
            isNews={isNews(buzz)}
            track={commentsTrackingFn}
            showEmojiReactions={showEmojiReactions}
            commentsEnabled={commentsEnabled}
          />
        </>
      )}
      {condensedComments && (
        <CondensedCommentsWrapper
          isCommentsPanelOpen={isCommentsPanelOpen}>
            {comments}
        </CondensedCommentsWrapper>
      )}
    </div>
  );
}

CommentsWrapper.propTypes = {
  buzz: PropTypes.object,
  firstPage: PropTypes.number,
  repliesFirstPage: PropTypes.number,
  perPage: PropTypes.number,
}

export default withAds(CommentsWrapper);
